import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { HeaderMenuItem, ButtonActionConfig, HeaderMenuTargetType } from '../../types';
import {
  X,
  Check,
  Plus,
  Trash2,
  Menu as MenuIcon,
  Link,
  Phone,
  MessageCircle,
  Mail,
  FileText,
  Hash,
  Globe,
  ChevronUp,
  ChevronDown,
  Sparkles,
  Sliders,
  Image as ImageIcon,
  ExternalLink,
  Loader2,
} from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onOpenImagePicker?: () => void;
}

export const HeaderSettingsModal: React.FC<Props> = ({ isOpen, onClose, onOpenImagePicker }) => {
  const { activeSite, updateSiteData } = useSite();

  const [logoText, setLogoText] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoWidth, setLogoWidth] = useState(140);
  const [phone, setPhone] = useState('');
  const [buttonText, setButtonText] = useState('Teklif Al');
  const [buttonActionType, setButtonActionType] = useState<ButtonActionConfig['type']>('form');
  const [buttonTarget, setButtonTarget] = useState('/iletisim');
  const [stickyHeader, setStickyHeader] = useState(true);
  const [headerTransparent, setHeaderTransparent] = useState(false);
  const [headerShadow, setHeaderShadow] = useState<'none' | 'sm' | 'md' | 'lg'>('sm');
  const [menuItems, setMenuItems] = useState<HeaderMenuItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (activeSite && isOpen) {
      setLogoText(activeSite.settings?.logoText || activeSite.settings?.businessName || activeSite.name || 'Adiosoft');
      setLogoUrl(activeSite.settings?.logoUrl || '');
      setLogoWidth(activeSite.settings?.logoWidth || 140);
      setPhone(activeSite.settings?.phone || '');
      setButtonText(activeSite.settings?.headerButtonText || (activeSite.industry === 'YACHT_RENTAL' ? 'Rezervasyon Yap' : 'Teklif Al'));
      setButtonActionType(activeSite.settings?.headerButtonAction?.type || 'page');
      setButtonTarget(activeSite.settings?.headerButtonAction?.target || (activeSite.industry === 'YACHT_RENTAL' ? '/rezervasyon' : '/iletisim'));
      setStickyHeader(activeSite.settings?.stickyHeader !== false);
      setHeaderTransparent(Boolean(activeSite.settings?.headerTransparent));
      setHeaderShadow(activeSite.settings?.headerShadow || 'sm');

      if (activeSite.settings?.headerMenu && activeSite.settings.headerMenu.length > 0) {
        setMenuItems(
          activeSite.settings.headerMenu.map((m, idx) => ({
            ...m,
            targetType: m.targetType || (m.type as HeaderMenuTargetType) || 'page',
            targetId: m.targetId || m.target || '',
            order: m.order ?? idx,
          }))
        );
      } else {
        // Generate default menu items from existing pages
        const defaultMenu: HeaderMenuItem[] = (activeSite.pages || []).map((p, idx) => ({
          id: p.id || `menu-${idx}`,
          label: p.title,
          targetType: 'page',
          type: 'page',
          targetId: p.slug,
          target: p.slug,
          order: idx,
        }));
        setMenuItems(defaultMenu);
      }
    }
  }, [activeSite, isOpen]);

  if (!isOpen || !activeSite) return null;

  const pages = activeSite.pages || [];

  // Collect all available sections across pages for anchor jumping
  const availableSections = pages.flatMap((p) =>
    (p.sections || []).map((s) => {
      const anchor = s.style?.anchorId || s.style?.customId || `${s.type.toLowerCase()}-section`;
      const anchorId = `#${anchor.replace(/^#/, '')}`;
      return {
        label: `${p.title} → ${s.content?.title || s.type} (${anchorId})`,
        value: anchorId,
      };
    })
  );

  const sectionOptions = availableSections.length > 0
    ? availableSections
    : [
        { label: 'Hizmetler (#services)', value: '#services' },
        { label: 'Hakkımızda (#about)', value: '#about' },
        { label: 'Ekip (#team-section)', value: '#team-section' },
        { label: 'İletişim (#contact)', value: '#contact' },
        { label: 'Yorumlar (#testimonials)', value: '#testimonials' },
        { label: 'SSS (#faq)', value: '#faq' },
      ];

  const handleAddMenuItem = () => {
    const newItem: HeaderMenuItem = {
      id: `menu_${Date.now()}`,
      label: 'Yeni Menü Öğesi',
      targetType: 'page',
      type: 'page',
      targetId: pages[0]?.slug || '/',
      target: pages[0]?.slug || '/',
      order: menuItems.length,
      openInNewTab: false,
    };
    setMenuItems([...menuItems, newItem]);
  };

  const handleUpdateMenuItem = (id: string, updates: Partial<HeaderMenuItem>) => {
    setMenuItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const next = { ...item, ...updates };

        if (updates.targetType) {
          next.type = updates.targetType as any;
        } else if (updates.type) {
          next.targetType = updates.type as any;
        }

        if (updates.targetId !== undefined) {
          next.target = updates.targetId;
        } else if (updates.target !== undefined) {
          next.targetId = updates.target;
        }
        if (updates.url !== undefined) {
          next.target = updates.url;
        }

        return next;
      })
    );
  };

  const handleDeleteMenuItem = (id: string) => {
    setMenuItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleMoveMenuItem = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= menuItems.length) return;
    const newItems = [...menuItems];
    const temp = newItems[index];
    newItems[index] = newItems[targetIndex];
    newItems[targetIndex] = temp;
    setMenuItems(newItems);
  };

  const handleSave = async () => {
    if (!activeSite) return;
    const updatedSettings = {
      ...(activeSite.settings || {}),
      logoText,
      logoUrl,
      logoWidth,
      phone,
      headerButtonText: buttonText,
      headerButtonAction: {
        type: buttonActionType,
        target: buttonTarget,
        targetPage: buttonActionType === 'page' ? buttonTarget : undefined,
        targetSection: buttonActionType === 'section' ? buttonTarget : undefined,
        externalUrl: buttonActionType === 'url' ? buttonTarget : undefined,
      },
      stickyHeader,
      headerTransparent,
      headerShadow,
      headerMenu: menuItems,
    };

    setIsSaving(true);
    try {
      await updateSiteData({ settings: updatedSettings });
      setSaveSuccess(true);
      setTimeout(() => {
        onClose();
      }, 400);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in">
      <div className="header-settings-modal bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden text-slate-100 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-5 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2 text-blue-400">
            <MenuIcon className="w-5 h-5" />
            <div>
              <h3 className="font-extrabold text-base text-white tracking-tight">Website Üst Menüsü & Header Ayarları</h3>
              <p className="text-xs text-slate-400">Logo, menü sıralaması, hedef linkler ve aksiyon butonu</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto custom-scrollbar flex-1">
          {/* Logo & Brand Name */}
          <div className="p-4 rounded-2xl bg-slate-800/60 border border-slate-700/60 space-y-3">
            <h4 className="text-xs font-bold text-slate-200 uppercase tracking-wider">Logo & Marka Bilgileri</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-200 block mb-1">Marka / Şirket Adı</label>
                <input
                  type="text"
                  value={logoText}
                  onChange={(e) => setLogoText(e.target.value)}
                  placeholder="Örn: Cihan Holding"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-300 text-xs font-semibold text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-xs transition"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-200 block mb-1">Logo Görsel URL</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    placeholder="https://.../logo.png"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-300 text-xs font-semibold text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-xs transition"
                  />
                  {onOpenImagePicker && (
                    <button
                      type="button"
                      onClick={onOpenImagePicker}
                      title="Görsel Seç"
                      className="px-3 py-2 rounded-xl bg-slate-700 hover:bg-slate-600 text-white shrink-0 text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer"
                    >
                      <ImageIcon className="w-4 h-4" />
                      <span className="hidden sm:inline">Görsel Seç</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Header Display Style Options */}
            <div className="pt-3 border-t border-slate-700/60 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex items-center gap-2.5 cursor-pointer text-xs font-bold text-slate-200 select-none py-1 group">
                <input
                  type="checkbox"
                  checked={stickyHeader}
                  onChange={(e) => setStickyHeader(e.target.checked)}
                  className="w-4 h-4 rounded text-blue-600 bg-white border border-slate-300 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-slate-200 group-hover:text-white transition">Sayfada Sabit Kalsın (Sticky Header)</span>
              </label>

              <label className="flex items-center gap-2.5 cursor-pointer text-xs font-bold text-slate-200 select-none py-1 group">
                <input
                  type="checkbox"
                  checked={headerTransparent}
                  onChange={(e) => setHeaderTransparent(e.target.checked)}
                  className="w-4 h-4 rounded text-blue-600 bg-white border border-slate-300 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-slate-200 group-hover:text-white transition">Şeffaf Header (Transparent)</span>
              </label>
            </div>
          </div>

          {/* Menu Items Management */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-xs font-bold text-slate-200 uppercase tracking-wider">Menü Öğeleri & Hedefler</h4>
                <p className="text-xs text-slate-400">Ziyaretçilerin göreceği menü linklerini düzenleyin</p>
              </div>
              <button
                type="button"
                onClick={handleAddMenuItem}
                className="px-3.5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition flex items-center gap-1.5 shadow-sm active:scale-95 cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Menü Öğesi Ekle</span>
              </button>
            </div>

            <div className="space-y-2.5">
              {menuItems.map((item, index) => {
                const effectiveTargetType = item.targetType || (item.type as HeaderMenuTargetType) || 'page';
                const effectiveTarget = item.targetId || item.target || '';

                return (
                  <div
                    key={item.id}
                    className="p-3.5 rounded-2xl bg-slate-800/80 border border-slate-700/80 flex flex-col sm:flex-row items-start sm:items-center gap-2.5 transition hover:border-slate-600 shadow-xs"
                  >
                    {/* Reorder buttons */}
                    <div className="flex items-center gap-1 text-slate-400 shrink-0">
                      <button
                        type="button"
                        disabled={index === 0}
                        onClick={() => handleMoveMenuItem(index, 'up')}
                        className="p-1.5 rounded-lg hover:bg-slate-700 hover:text-white disabled:opacity-20 transition cursor-pointer"
                        title="Yukarı Taşı"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        disabled={index === menuItems.length - 1}
                        onClick={() => handleMoveMenuItem(index, 'down')}
                        className="p-1.5 rounded-lg hover:bg-slate-700 hover:text-white disabled:opacity-20 transition cursor-pointer"
                        title="Aşağı Taşı"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Menu Label */}
                    <div className="w-full sm:w-40 shrink-0">
                      <input
                        type="text"
                        value={item.label}
                        onChange={(e) => handleUpdateMenuItem(item.id, { label: e.target.value })}
                        placeholder="Menü Adı"
                        className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-xs font-semibold text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-xs"
                      />
                    </div>

                    {/* Link Target Type Selector */}
                    <div className="w-full sm:w-40 shrink-0">
                      <select
                        value={effectiveTargetType}
                        onChange={(e) =>
                          handleUpdateMenuItem(item.id, {
                            targetType: e.target.value as HeaderMenuTargetType,
                            type: e.target.value as any,
                          })
                        }
                        className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-xs font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-xs cursor-pointer"
                      >
                        <option value="page" className="bg-white text-slate-900">Sayfaya Git</option>
                        <option value="section" className="bg-white text-slate-900">Bölüme Kay (#)</option>
                        <option value="external" className="bg-white text-slate-900">Harici Web Sitesi</option>
                        <option value="phone" className="bg-white text-slate-900">Telefon Ara</option>
                        <option value="whatsapp" className="bg-white text-slate-900">WhatsApp Hattı</option>
                        <option value="email" className="bg-white text-slate-900">E-posta Gönder</option>
                      </select>
                    </div>

                    {/* Target Value input based on Target Type */}
                    <div className="flex-1 w-full sm:w-auto">
                      {effectiveTargetType === 'page' ? (
                        <select
                          value={effectiveTarget}
                          onChange={(e) =>
                            handleUpdateMenuItem(item.id, { targetId: e.target.value, target: e.target.value })
                          }
                          className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-xs font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-xs cursor-pointer"
                        >
                          {pages.map((p) => (
                            <option key={p.id} value={p.slug} className="bg-white text-slate-900">
                              {p.title} ({p.slug})
                            </option>
                          ))}
                        </select>
                      ) : effectiveTargetType === 'section' ? (
                        <select
                          value={effectiveTarget}
                          onChange={(e) =>
                            handleUpdateMenuItem(item.id, { targetId: e.target.value, target: e.target.value })
                          }
                          className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-xs font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-xs cursor-pointer"
                        >
                          {sectionOptions.map((opt, sIdx) => (
                            <option key={sIdx} value={opt.value} className="bg-white text-slate-900">
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={item.url || effectiveTarget}
                            onChange={(e) =>
                              handleUpdateMenuItem(item.id, {
                                targetId: e.target.value,
                                target: e.target.value,
                                url: e.target.value,
                              })
                            }
                            placeholder={
                              effectiveTargetType === 'phone'
                                ? '+90 5XX...'
                                : effectiveTargetType === 'whatsapp'
                                ? '905XXXXXXXXX'
                                : effectiveTargetType === 'email'
                                ? 'info@alanadiniz.com'
                                : 'https://...'
                            }
                            className="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-xs font-semibold text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-xs"
                          />
                          {effectiveTargetType === 'external' && (
                            <label
                              className="flex items-center gap-1.5 text-xs text-slate-200 whitespace-nowrap cursor-pointer shrink-0 font-medium select-none"
                              title="Yeni Sekmede Aç"
                            >
                              <input
                                type="checkbox"
                                checked={item.openInNewTab !== false}
                                onChange={(e) =>
                                  handleUpdateMenuItem(item.id, { openInNewTab: e.target.checked })
                                }
                                className="w-3.5 h-3.5 rounded text-blue-600 bg-white border border-slate-300 focus:ring-blue-500 cursor-pointer"
                              />
                              <span>Yeni Sekme</span>
                            </label>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Delete Item */}
                    <button
                      type="button"
                      onClick={() => handleDeleteMenuItem(item.id)}
                      className="p-2 rounded-xl text-slate-400 hover:text-rose-400 hover:bg-rose-500/15 transition self-end sm:self-center shrink-0 cursor-pointer"
                      title="Menü Öğesini Kaldır"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Action Button Settings */}
          <div className="p-4 rounded-2xl bg-slate-800/60 border border-slate-700/60 space-y-3">
            <h4 className="text-xs font-bold text-slate-200 uppercase tracking-wider">Sağ Aksiyon / Teklif Butonu</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-200 block mb-1">Buton Metni</label>
                <input
                  type="text"
                  value={buttonText}
                  onChange={(e) => setButtonText(e.target.value)}
                  placeholder="Teklif Al"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-300 text-xs font-semibold text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-xs"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-200 block mb-1">Aksiyon Türü</label>
                <select
                  value={buttonActionType}
                  onChange={(e) => setButtonActionType(e.target.value as any)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-300 text-xs font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-xs cursor-pointer"
                >
                  <option value="page" className="bg-white text-slate-900">Sayfaya Git</option>
                  <option value="section" className="bg-white text-slate-900">Bölüme Kay</option>
                  <option value="form" className="bg-white text-slate-900">İletişim Formuna Git</option>
                  <option value="phone" className="bg-white text-slate-900">Telefon Et</option>
                  <option value="whatsapp" className="bg-white text-slate-900">WhatsApp Mesajı</option>
                  <option value="url" className="bg-white text-slate-900">Özel URL</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-200 block mb-1">Hedef</label>
                {buttonActionType === 'page' ? (
                  <select
                    value={buttonTarget}
                    onChange={(e) => setButtonTarget(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-300 text-xs font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-xs cursor-pointer"
                  >
                    {pages.map((p) => (
                      <option key={p.id} value={p.slug} className="bg-white text-slate-900">
                        {p.title}
                      </option>
                    ))}
                  </select>
                ) : buttonActionType === 'section' ? (
                  <select
                    value={buttonTarget}
                    onChange={(e) => setButtonTarget(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-300 text-xs font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-xs cursor-pointer"
                  >
                    <option value="#contact" className="bg-white text-slate-900">#contact</option>
                    <option value="#services" className="bg-white text-slate-900">#services</option>
                    <option value="#about" className="bg-white text-slate-900">#about</option>
                    <option value="#cta" className="bg-white text-slate-900">#cta</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    value={buttonTarget}
                    onChange={(e) => setButtonTarget(e.target.value)}
                    placeholder="Hedef..."
                    className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-300 text-xs font-semibold text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-xs"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800 flex items-center justify-end gap-3 bg-slate-900/90">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-300 hover:text-white hover:bg-slate-800 transition cursor-pointer"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="px-5 py-2.5 rounded-xl text-xs font-bold bg-blue-600 hover:bg-blue-500 text-white shadow-lg flex items-center gap-2 transition active:scale-95 disabled:opacity-75 cursor-pointer"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Kaydediliyor...</span>
              </>
            ) : saveSuccess ? (
              <>
                <Check className="w-4 h-4 text-emerald-400" />
                <span>Kaydedildi</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                <span>Header'ı Güncelle</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
